import * as React from 'react'
import { useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'lib/utils'

const toastVariants = cva(
	'group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-4 pr-8 shadow-lg transition-all data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-top-full data-[state=closed]:slide-out-to-right-full',
	{
		variants: {
			variant: {
				default: 'border bg-background text-foreground',
				success: 'border-success bg-success text-success-foreground',
				destructive: 'border-destructive bg-destructive text-destructive-foreground',
				warning: 'border-warning bg-warning text-warning-foreground',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
)

export interface ToastProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof toastVariants> {
	open?: boolean
	onClose?: () => void
	duration?: number
}

const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
	({ className, variant, open = true, onClose, duration = 5000, children, ...props }, ref) => {
		const handleClose = useCallback(() => {
			onClose?.()
		}, [onClose])

		useEffect(() => {
			if (!open || !duration) return
			const timer = setTimeout(handleClose, duration)
			return () => clearTimeout(timer)
		}, [open, duration, handleClose])

		if (!open) return null

		return ReactDOM.createPortal(
			<div className="fixed top-4 right-4 z-[10003] flex flex-col gap-2">
				<div
					ref={ref}
					data-state={open ? 'open' : 'closed'}
					className={cn(toastVariants({ variant }), className)}
					{...props}
				>
					<div className="flex-1">{children}</div>
					<button
						type="button"
						onClick={handleClose}
						className="absolute right-2 top-2 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100"
					>
						<svg
							className="h-4 w-4"
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="M18 6 6 18" />
							<path d="m6 6 12 12" />
						</svg>
					</button>
				</div>
			</div>,
			document.body,
		)
	},
)
Toast.displayName = 'Toast'

const ToastTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
	({ className, ...props }, ref) => (
		<p ref={ref} className={cn('text-sm font-semibold', className)} {...props} />
	),
)
ToastTitle.displayName = 'ToastTitle'

const ToastDescription = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<p ref={ref} className={cn('text-sm opacity-90', className)} {...props} />
))
ToastDescription.displayName = 'ToastDescription'

export { Toast, ToastTitle, ToastDescription, toastVariants }
